const DEFAULT_RISK_FREE_RATE = 0.0;

const aggregateDataByDay = (dataWithDates) => {
  const dailyDataMap = new Map();

  for (const item of dataWithDates) {
    const dateKey = item.date.toISOString().split("T")[0];
    if (!dailyDataMap.has(dateKey)) {
      dailyDataMap.set(dateKey, []);
    }
    dailyDataMap.get(dateKey).push(item.value);
  }

  const aggregated = [];
  for (const [dateKey, values] of dailyDataMap.entries()) {
    const avgValue = values.reduce((sum, val) => sum + val, 0) / values.length;
    aggregated.push({
      date: new Date(dateKey),
      value: avgValue,
    });
  }

  aggregated.sort((a, b) => a.date - b.date);
  return aggregated;
};

const calculateSharpeRatio = (
  growthValues,
  daysOfOperation,
  riskFreeRate = DEFAULT_RISK_FREE_RATE,
  dates = null
) => {
  if (!growthValues || growthValues.length < 2) {
    return null;
  }

  const dailyReturns = [];
  const dailyReturnIndices = [];
  const dailyReturnsWithDates = [];

  for (let i = 1; i < growthValues.length; i++) {
    const prevValue = growthValues[i - 1];
    const currValue = growthValues[i];
    if (prevValue > 0 && Number.isFinite(prevValue) && Number.isFinite(currValue)) {
      const dailyReturn = (currValue / prevValue - 1) * 100;
      if (Number.isFinite(dailyReturn)) {
        dailyReturns.push(dailyReturn);
        dailyReturnIndices.push(i);
        if (dates && dates[i]) {
          dailyReturnsWithDates.push({
            date: dates[i],
            return: dailyReturn,
            index: i,
          });
        }
      }
    }
  }

  if (dailyReturns.length < 2) {
    return null;
  }

  const OUTLIER_THRESHOLD = 7.0;
  const outlierIndices = new Set();

  const dailyReturnsFiltered = [];
  const filteredIndices = [];

  for (let i = 0; i < dailyReturns.length; i++) {
    const returnValue = dailyReturns[i];
    if (returnValue >= -OUTLIER_THRESHOLD && returnValue <= OUTLIER_THRESHOLD) {
      dailyReturnsFiltered.push(returnValue);
      filteredIndices.push(dailyReturnIndices[i]);
    } else {
      outlierIndices.add(dailyReturnIndices[i]);
    }
  }

  const outliersRemoved = dailyReturns.length - dailyReturnsFiltered.length;
  const originalDailyReturnsCount = dailyReturns.length;
  const daysOfOperationFiltered = dailyReturnsFiltered.length + 1;
  const actualDaysOfOperation = growthValues.length;

  const initialValue = growthValues[0];
  const endValue = growthValues[growthValues.length - 1];
  const absoluteReturn = ((endValue - initialValue) / initialValue) * 100;
  const annualizedReturn = (absoluteReturn / daysOfOperationFiltered) * 365;

  const meanReturn =
    dailyReturnsFiltered.reduce((sum, val) => sum + val, 0) /
    dailyReturnsFiltered.length;
  const variance =
    dailyReturnsFiltered.reduce((sum, val) => sum + (val - meanReturn) ** 2, 0) /
    (dailyReturnsFiltered.length - 1);
  const dailyVolatility = Math.sqrt(variance);

  const fullPeriodVolatility = dailyVolatility * Math.sqrt(daysOfOperationFiltered);
  const annualizedVolatility = dailyVolatility * Math.sqrt(365);

  const sharpeFullPeriod =
    fullPeriodVolatility !== 0
      ? (absoluteReturn - riskFreeRate) / fullPeriodVolatility
      : null;
  const sharpeAnnualized =
    annualizedVolatility !== 0
      ? (annualizedReturn - riskFreeRate) / annualizedVolatility
      : null;

  return {
    sharpeFullPeriod,
    sharpeAnnualized,
    absoluteReturn,
    annualizedReturn,
    dailyVolatility,
    fullPeriodVolatility,
    annualizedVolatility,
    daysOfOperation: daysOfOperationFiltered,
    daysOfOperationOriginal: daysOfOperation,
    actualDaysOfOperation,
    originalDailyReturnsCount,
    filteredDailyReturnsCount: dailyReturnsFiltered.length,
    outliersRemoved,
    dailyReturns: dailyReturnsFiltered,
    rawDailyReturns: dailyReturns,
    dailyReturnsWithDates,
    filteredIndices,
    outlierIndices: Array.from(outlierIndices),
    initialValue,
    endValue,
  };
};

export const computeSharpeRatioFromDailyGrowth = (labels, growthValues) => {
  if (!Array.isArray(labels) || !Array.isArray(growthValues)) {
    return null;
  }

  const endDate = new Date();
  const startDate = new Date("2024-07-23");

  const filteredValues = [];
  const filteredLabels = [];
  for (let i = 0; i < labels.length; i++) {
    const recordDate = new Date(labels[i]);
    if (recordDate >= startDate && recordDate <= endDate) {
      filteredValues.push(growthValues[i]);
      filteredLabels.push(labels[i]);
    }
  }

  const daysOfOperation =
    Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  const dataWithDates = [];
  for (let i = 0; i < filteredValues.length; i++) {
    const parsed =
      typeof filteredValues[i] === "number"
        ? filteredValues[i]
        : parseFloat(filteredValues[i]);
    if (Number.isFinite(parsed) && parsed > 0) {
      dataWithDates.push({
        value: parsed,
        date: new Date(filteredLabels[i]),
      });
    }
  }

  const dailyAggregated = aggregateDataByDay(dataWithDates);
  const dailyGrowthValues = dailyAggregated.map((item) => item.value);
  const dailyDates = dailyAggregated.map((item) => item.date);

  const ratioResult = calculateSharpeRatio(
    dailyGrowthValues,
    daysOfOperation,
    DEFAULT_RISK_FREE_RATE,
    dailyDates
  );

  return ratioResult?.sharpeAnnualized ?? null;
};
